import { LOW_CONFIDENCE_THRESHOLD, SegmentServerStatus } from './AiBatchIntake.constants';

/** The API may return confidence as a 0–100 integer or 0.0–1.0 float. Normalize to 0–1. */
const CONFIDENCE_PERCENT_SCALE = 100;

/**
 * Determine whether a segment's confidence should be flagged for review.
 * Returns true when confidence is null/undefined (unknown — treat as needs-review)
 * or when the normalized value falls below the review threshold.
 * The backend may return confidence as 0–100 or 0.0–1.0; normalise to 0.0–1.0.
 *
 * @param {number|null|undefined} confidence
 * @returns {boolean}
 */
export const isSegmentLowConfidence = confidence => {
  if (confidence == null) {
    return true;
  }

  const normalized = confidence > 1 ? confidence / CONFIDENCE_PERCENT_SCALE : confidence;

  return normalized < LOW_CONFIDENCE_THRESHOLD;
};

/**
 * Format a 0–100 or 0.0–1.0 confidence value as a human-readable percentage string.
 * Returns 'Not scored' for null/undefined (no confidence data from the backend).
 *
 * @param {number|null|undefined} confidence
 * @returns {string}
 */
export const formatConfidencePercent = confidence => {
  if (confidence == null) {
    return 'Not scored';
  }

  const pct =
    confidence > 1 ? Math.round(confidence) : Math.round(confidence * CONFIDENCE_PERCENT_SCALE);

  return `${pct}%`;
};

/**
 * Build the display label for a page range segment.
 *
 * @param {number} pageStart - 1-based
 * @param {number} pageEnd   - 1-based (inclusive)
 * @returns {string}
 */
export const formatPageRange = (pageStart, pageEnd) => {
  if (pageStart === pageEnd) {
    return `p. ${pageStart}`;
  }

  return `pp. ${pageStart}–${pageEnd}`;
};

/**
 * Build the location line shown below the segment title in the review list.
 *
 * DEV-4526/DEV-4527: for background-reviewed segments the backend now returns
 * a `sourceFileName` per segment. When both a page range AND a source filename
 * are available (multi-source submission), both are shown. When only one is
 * available, only that one is shown.
 *
 * DEV-4598: when a segment has been merged across multiple source files
 * (`sourceCount > 1`), the label shows the page span plus a source count
 * ("pp. 1–5 · 2 sources") instead of collapsing to the first file's name.
 * Single-source / single-page segments are unchanged.
 *
 * @param {{
 *   pageStart: number|null,
 *   pageEnd: number|null,
 *   sourceFileName: string|null,
 *   sourceCount?: number|null,
 * }} seg
 * @returns {string}
 */
export const formatSegmentLocation = ({ pageStart, pageEnd, sourceFileName, sourceCount }) => {
  const pageText =
    pageStart != null ? `from packet · ${formatPageRange(pageStart, pageEnd)}` : null;

  // When merged across > 1 source file, show a count rather than a single file name
  // so the label does not mislead the user into thinking only one file contributed.
  const isMultiSource = (sourceCount ?? 1) > 1;
  const sourceText = isMultiSource ? `${sourceCount} sources` : (sourceFileName ?? null);

  if (pageText && sourceText) {
    return `${pageText} · ${sourceText}`;
  }

  return pageText ?? sourceText ?? 'unknown source';
};

/**
 * Derive the subtitle text for the review header.
 *
 * @param {{
 *   isBackgroundReview: boolean,
 *   isSinglePdf: boolean,
 *   segments: object[],
 *   segPlural: string,
 *   uploadLabel?: string|null,
 * }} params
 * @returns {string}
 */
export const deriveSubtitleText = ({
  isBackgroundReview,
  isSinglePdf,
  segments,
  segPlural,
  uploadLabel,
}) => {
  if (isBackgroundReview) {
    const source = uploadLabel ? ` from ${uploadLabel}` : ' from combined PDF';

    return `AI processed ${segments.length} document${segPlural}${source}`;
  }

  if (isSinglePdf) {
    return `AI split the packet into ${segments.length} document${segPlural} and categorized each`;
  }

  return `AI categorized ${segments.length} document${segPlural}`;
};

/**
 * A segment is "unrecognized" when the backend could not classify it (no
 * documentTypeId) and it did not FAIL outright — Failed segments are also
 * document-less but are surfaced through the separate failedSegments count, so
 * counting them here would double-count and could hold the drawer open forever.
 * Once the user assigns a type the segment is no longer unrecognized, so the
 * review-header count derived from this shrinks live.
 *
 * @param {{ documentTypeId?: string|null, serverStatus?: string }} seg
 * @returns {boolean}
 */
export const isSegmentUnrecognized = seg =>
  seg?.documentTypeId == null && seg?.serverStatus !== SegmentServerStatus.FAILED;

/**
 * Derive the completion-banner text for a completed intake job. Reads the
 * backend UnrecognizedSegments count (null = plan not ready, 0 = clean) and the
 * companion FailedSegments count — both null-safe. Returns null when the job
 * completed clean (nothing unrecognized or failed) so the caller renders no banner.
 *
 * @param {{ unrecognizedSegments?: number|null, failedSegments?: number|null }} params
 * @returns {string|null}
 */
export const deriveIntakeCompletionText = ({ unrecognizedSegments, failedSegments } = {}) => {
  const unrecognized = unrecognizedSegments ?? 0;
  const failed = failedSegments ?? 0;
  const parts = [];

  if (unrecognized > 0) {
    parts.push(`${unrecognized} unrecognized segment${unrecognized === 1 ? '' : 's'}`);
  }

  if (failed > 0) {
    parts.push(`${failed} failed segment${failed === 1 ? '' : 's'}`);
  }

  if (parts.length === 0) {
    return null;
  }

  return `Completed with ${parts.join(' and ')}`;
};

/**
 * Normalize a raw parts array: null unless it is a non-empty array.
 *
 * @param {object[]|null|undefined} parts
 * @returns {object[]|null}
 */
const normalizeParts = parts => (Array.isArray(parts) && parts.length > 0 ? parts : null);

/**
 * Build UI segments from a background intake job's resultJson.
 * These segments have no local `file` — filing uses the server-side job reference.
 *
 * DEV-4526: includes `sourceFileName` from the segment plan so the review drawer
 * can show which uploaded file each segment came from.
 * DEV-4555: includes `sourceIndex` for the content-preview endpoint.
 *
 * The plan's parts carry only (sourceIndex, pageIndex) — no file names — while
 * each SEGMENT names its own source (grouped segments name their FIRST part's
 * source). A job-wide sourceIndex → sourceFileName map is built from the
 * segments and stamped onto each part so grouped previews can show real file
 * names where the plan knows them; unnamed sources fall back to "Photo N".
 *
 * The plan may also omit a segment-level sourceIndex (plans written before
 * DEV-4527) — resolve it from the first part here, at build time, so every
 * consumer (preview loader, reclassify, filing overrides) sees the correct
 * source rather than a blanket 0.
 *
 * Segments flagged as a possible duplicate (`duplicateOf` present) start
 * DESELECTED so a re-upload is skipped by default; the user can re-enable
 * the file-toggle to file it anyway.
 *
 * @param {{
 *   segments: Array<{
 *     startPageIndex: number,
 *     endPageIndex: number,
 *     documentTypeId: string|null,
 *     confidence: number|null,
 *     status: string,
 *     sourceFileName?: string|null,
 *     sourceIndex?: number|null,
 *     parts?: Array<{ sourceIndex: number, pageIndex: number }>|null,
 *     duplicateOf?: { jobId: string, documentId?: string|null, filedDate?: string|null }|null,
 *   }>,
 * }} resultJson
 * @param {string} jobId
 * @returns {object[]}
 */
export const buildSegmentsFromResultJson = (resultJson, jobId) => {
  const segments = resultJson?.segments ?? [];

  const namesBySourceIndex = new Map();

  segments.forEach(segment => {
    if (
      segment.sourceIndex != null &&
      segment.sourceFileName != null &&
      !namesBySourceIndex.has(segment.sourceIndex)
    ) {
      namesBySourceIndex.set(segment.sourceIndex, segment.sourceFileName);
    }
  });

  return segments.map((segment, index) => {
    const parts =
      normalizeParts(segment.parts)?.map(part => ({
        ...part,
        sourceFileName: part.sourceFileName ?? namesBySourceIndex.get(part.sourceIndex) ?? null,
      })) ?? null;

    return {
      id: `bg-${jobId}-seg-${index}`,
      pageStart: segment.startPageIndex + 1,
      pageEnd: segment.endPageIndex + 1,
      // 0-based original start/end page indices (backend coordinates) — stored
      // separately so boundary edits can compute override values to send on file.
      originalStartPageIndex: segment.startPageIndex,
      originalEndPageIndex: segment.endPageIndex,
      splitConfidence: null,
      truncated: false,
      sourceFileName: segment.sourceFileName ?? null,
      sourceIndex: segment.sourceIndex ?? parts?.[0]?.sourceIndex ?? 0,
      parts,
      duplicateOf: segment.duplicateOf ?? null,
      file: null,
      jobId,
      segmentIndex: index,
      documentTypeId: segment.documentTypeId ?? null,
      classifyConfidence: segment.confidence ?? null,
      serverStatus: segment.status,
      isSelected: segment.duplicateOf == null,
      isRemoved: false,
      isEdited: false,
      editedPageStart: null,
      editedPageEnd: null,
    };
  });
};

/**
 * Distinct source indices contributing to a segment's `parts`, in
 * first-appearance order. Empty when the segment has no parts.
 *
 * @param {object} seg
 * @returns {number[]}
 */
export const getGroupedSourceIndices = seg => [
  ...new Set((seg?.parts ?? []).map(part => part.sourceIndex)),
];

/**
 * A segment is "grouped" when its parts span more than one source file
 * (several photos assembled into one logical document).
 *
 * @param {object} seg
 * @returns {boolean}
 */
export const isGroupedSegment = seg => getGroupedSourceIndices(seg).length > 1;

/**
 * Label for the grouping indicator chip on a grouped segment card.
 *
 * @param {object} seg
 * @returns {string}
 */
export const formatGroupingIndicator = seg =>
  `${getGroupedSourceIndices(seg).length} photos → 1 document`;

/**
 * Display name for a single part of a grouped segment. `sourceFileName` is
 * stamped client-side by buildSegmentsFromResultJson (the plan's parts carry
 * only source/page indices); parts whose source the plan never named fall
 * back to a positional "Photo N" label.
 *
 * @param {{ sourceIndex: number, sourceFileName?: string|null }} part
 * @returns {string}
 */
export const getPartFileName = part => part.sourceFileName ?? `Photo ${part.sourceIndex + 1}`;

/**
 * Distinct contributing file names for a grouped segment, one per source,
 * in first-appearance order.
 *
 * @param {object} seg
 * @returns {string[]}
 */
export const getContributingFileNames = seg => {
  const namesBySource = new Map();

  (seg?.parts ?? []).forEach(part => {
    if (!namesBySource.has(part.sourceIndex)) {
      namesBySource.set(part.sourceIndex, getPartFileName(part));
    }
  });

  return [...namesBySource.values()];
};

/**
 * Expand a segment into its per-page parts. Current plans carry `parts` for
 * every staged segment (DEV-4799); for segments without them (older plans,
 * local splits), synthesize one part per effective page against the segment's
 * own source so a merge can combine grouped and plain neighbours.
 *
 * @param {object} seg
 * @returns {Array<{ sourceIndex: number, pageIndex: number, sourceFileName: string|null }>}
 */
const expandSegmentToParts = seg => {
  if (seg.parts != null) {
    return seg.parts;
  }

  const { startPageIndex, endPageIndex } = getEffectivePageIndices(seg);

  return Array.from({ length: endPageIndex - startPageIndex + 1 }, (_, i) => ({
    sourceIndex: seg.sourceIndex ?? 0,
    pageIndex: startPageIndex + i,
    sourceFileName: seg.sourceFileName ?? null,
  }));
};

/**
 * Merge segment at `idx` with the one immediately AFTER it (merge-with-next).
 * The merged segment spans from seg[idx].pageStart to seg[idx+1].pageEnd.
 * Confidence is cleared (requires re-classify). DocumentTypeId is taken from
 * the earlier segment, falling back to the later one.
 *
 * DEV-4598: track the number of distinct source files the merged segment spans
 * via a `sourceCount` field. When curr and next come from different source files
 * (or either is already a multi-source merge), sourceCount accumulates so that
 * formatSegmentLocation can label them "N sources" instead of collapsing to a
 * single misleading filename.
 *
 * Grouping metadata (DEV-4799) and duplicate flags (DEV-4800) can sit on
 * EITHER side of a merge, so they are reconciled rather than inherited from
 * curr alone: parts concatenate (expanding a part-less side from its page
 * range) so the grouped preview shows every page the merged segment covers;
 * duplicateOf marks a segment's source BYTES as previously submitted, so the
 * merged segment stays flagged when either side was, and the skip-by-default
 * selection survives (both sides must have been selected).
 *
 * @param {object[]} segments
 * @param {number} idx
 * @returns {object[]}
 */
export const mergeSegmentWithNext = (segments, idx) => {
  if (idx >= segments.length - 1) {
    return segments;
  }

  const curr = segments[idx];
  const next = segments[idx + 1];

  // Compute combined sourceCount. Each segment starts at 1 (single source from
  // buildSegmentsFromResultJson). A prior merge may have set sourceCount > 1.
  // When the two source files differ, add the next's count; when they are the
  // same file we do not double-count.
  const currCount = curr.sourceCount ?? 1;
  const nextCount = next.sourceCount ?? 1;
  const sameSource = curr.sourceFileName === next.sourceFileName;
  const mergedSourceCount = sameSource ? currCount : currCount + nextCount;

  const mergedParts =
    curr.parts == null && next.parts == null
      ? null
      : [...expandSegmentToParts(curr), ...expandSegmentToParts(next)];

  const merged = {
    ...curr,
    pageEnd: next.pageEnd,
    editedPageEnd: next.editedPageEnd ?? next.pageEnd,
    classifyConfidence: null,
    documentTypeId: curr.documentTypeId ?? next.documentTypeId,
    isEdited: true,
    sourceCount: mergedSourceCount,
    parts: mergedParts,
    duplicateOf: curr.duplicateOf ?? next.duplicateOf ?? null,
    isSelected: !!(curr.isSelected && next.isSelected),
  };

  return [...segments.slice(0, idx), merged, ...segments.slice(idx + 2)];
};

/**
 * Merge segment at `idx` with the one immediately BEFORE it (merge-with-prev).
 * Equivalent to mergeSegmentWithNext called at idx-1.
 *
 * @param {object[]} segments
 * @param {number} idx
 * @returns {object[]}
 */
export const mergeSegmentWithPrev = (segments, idx) => {
  if (idx <= 0) {
    return segments;
  }

  return mergeSegmentWithNext(segments, idx - 1);
};

/**
 * Split segment at `idx` immediately BEFORE `splitBeforePage` (1-based page number).
 * The head keeps pages [pageStart, splitBeforePage-1].
 * A new tail segment covers [splitBeforePage, pageEnd].
 *
 * Parts (DEV-4799) are in reading order, one per covered page, so the cut
 * offset within the page range partitions them between head and tail. The
 * duplicateOf flag (DEV-4800) marks the segment's source BYTES as previously
 * submitted — both fragments still come from those bytes, so both keep it.
 *
 * @param {object[]} segments
 * @param {number} idx - index of the segment to split
 * @param {number} splitBeforePage - 1-based page number at which the cut starts
 * @returns {object[]}
 */
export const splitSegmentBefore = (segments, idx, splitBeforePage) => {
  const seg = segments[idx];

  // Guard: can only split if splitBeforePage is strictly between pageStart and pageEnd.
  if (splitBeforePage <= seg.pageStart || splitBeforePage > seg.pageEnd) {
    return segments;
  }

  const tailStartPage = splitBeforePage;
  const headPartCount = splitBeforePage - seg.pageStart;
  const head = {
    ...seg,
    pageEnd: splitBeforePage - 1,
    editedPageEnd: splitBeforePage - 1,
    classifyConfidence: null,
    isEdited: true,
    parts: normalizeParts(seg.parts?.slice(0, headPartCount)),
  };
  const tail = {
    ...seg,
    // Assign a new local id so each segment has a stable key.
    id: `${seg.id}-split-${splitBeforePage}`,
    pageStart: tailStartPage,
    editedPageStart: tailStartPage,
    classifyConfidence: null,
    documentTypeId: null,
    isEdited: true,
    parts: normalizeParts(seg.parts?.slice(headPartCount)),
  };

  return [...segments.slice(0, idx), head, tail, ...segments.slice(idx + 1)];
};

/**
 * Mark a segment as removed (excluded from filing). The segment remains in the
 * list so it can be restored.
 *
 * @param {object[]} segments
 * @param {string} segId
 * @returns {object[]}
 */
export const removeSegment = (segments, segId) =>
  segments.map(s => (s.id === segId ? { ...s, isRemoved: true, isSelected: false } : s));

/**
 * Restore a previously-removed segment.
 *
 * @param {object[]} segments
 * @param {string} segId
 * @returns {object[]}
 */
export const restoreSegment = (segments, segId) =>
  segments.map(s => (s.id === segId ? { ...s, isRemoved: false, isSelected: true } : s));

/**
 * Derive the effective start/end page indices (0-based, for sending to the backend)
 * for a segment, taking into account any boundary edits.
 *
 * @param {object} seg - UI segment
 * @returns {{ startPageIndex: number, endPageIndex: number }}
 */
export const getEffectivePageIndices = seg => {
  // editedPageStart/End are 1-based UI values; convert to 0-based for the backend.
  const start = (seg.editedPageStart ?? seg.pageStart) - 1;
  const end = (seg.editedPageEnd ?? seg.pageEnd) - 1;

  return { startPageIndex: start, endPageIndex: end };
};

/**
 * Format the edited page range badge shown on a modified segment (e.g. "edited · pp. 2–4").
 *
 * @param {object} seg
 * @returns {string|null}
 */
export const formatEditedBadge = seg => {
  if (!seg.isEdited) {
    return null;
  }

  const start = seg.editedPageStart ?? seg.pageStart;
  const end = seg.editedPageEnd ?? seg.pageEnd;

  return `edited · ${formatPageRange(start, end)}`;
};
